import {IReflect} from '@ispm/dynamic-form/interfaces/reflect.interface';
import {get, merge} from 'lodash';
import {IdynamicFormItem} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import {VALUE_TYPES} from '@ispm/dynamic-form/constants/value-type.enum';
import {ConditionalItem} from '@ispm/shared/classes/conditional-item.class';
import {InterpolateService} from '@ispm/shared/services/interpolate.service';
import {FormulaService} from '@ispm/shared/services/formula.service';

const MIN_VALUE_PATH = 'applyNumericCheck.min';
const MAX_VALUE_PATH = 'applyNumericCheck.max';
const EXCLUSIVE_MIN_PATH = 'applyNumericCheck.exclusiveMin';
const EXCLUSIVE_MAX_PATH = 'applyNumericCheck.exclusiveMax';
const DISPLAY_ATTRIBUTE_CONFIG_PATH = 'valueProperties.display';
const DISPLAY_ATTRIBUTE_DEFAULT_PATH = 'display';

export function getFormItemValue(formValues: { [prop: string]: any }, formItemConfig: IdynamicFormItem) {
  if (!formItemConfig) {
    return formValues;
  } else if (formItemConfig.isRootProperty) {
    if ((<string>formItemConfig.valueType).toUpperCase() === VALUE_TYPES.ARRAY) {
      return (<any>Object).values(formValues);
    } else {
      return formValues;
    }
  } else {
    return get(formValues, formItemConfig.name);
  }
}

export function getDisplayedValue(formItemValue: any, formItemConfig: IdynamicFormItem) {
  if (formItemConfig && Array.isArray(formItemValue)) {
    return formItemValue.map(item => getDisplayedValue(item, formItemConfig));
  } else if (typeof formItemValue === 'object' && formItemConfig) {
    const path = get(formItemConfig, DISPLAY_ATTRIBUTE_CONFIG_PATH, DISPLAY_ATTRIBUTE_DEFAULT_PATH);
    formItemValue = get(formItemValue, path);
  }

  return formItemValue;
}

export function checkMin(reflect: IReflect, value: number) {
  const minValue = get(reflect, MIN_VALUE_PATH, null);
  const exclusiveMin: boolean = get(reflect, EXCLUSIVE_MIN_PATH, false);

  if (minValue !== null) {
    if (exclusiveMin) {
      return minValue < value;
    }

    return minValue <= value;
  }

  return true;
}

export function checkMax(reflect: IReflect, value: number) {
  const maxValue = get(reflect, MAX_VALUE_PATH, null);
  const exclusiveMax: boolean = get(reflect, EXCLUSIVE_MAX_PATH, false);

  if (maxValue !== null) {
    if (exclusiveMax) {
      return maxValue > value;
    }

    return maxValue >= value;
  }

  return true;
}

export function validateRegex(reflect: IReflect, value: any) {
  if (reflect.applyRegex == null) {
    return true;
  }

  const regExp = new RegExp(reflect.applyRegex, 'i');
  return regExp.test(value);
}

export function checkConditions(reflect: IReflect, value: any) {
  if (!reflect.conditions) {
    return true;
  }

  const reflectOutput = [];
  reflect.conditions.forEach((condition: ConditionalItem) => {
    const conditionalItem = ConditionalItem.from(value, condition);

    if (conditionalItem.verify()) {
      reflectOutput.push(true);
    } else {
      reflectOutput.push(false);
    }
  });

  return reflectOutput.every(val => !!val);
}

export function setCssClass(reflect: IReflect, value: any, reflectedItem: IdynamicFormItem) {
  if (!reflectedItem) {
    return;
  }

  if (reflect.styleClass !== undefined) {
    if (reflect.styleClass === '') {
      reflectedItem.additional.reflectStyleClasses = '';
    } else {
      reflectedItem.additional.reflectStyleClasses = reflectedItem.additional.reflectStyleClasses || ' ';
      reflectedItem.additional.reflectStyleClasses += ` ${reflect.styleClass} ` || ' ';
    }
  }

  if (reflect.styleClassPath !== undefined) {
    reflectedItem.additional.reflectStyleClasses += get(value, reflect.styleClassPath, ' ');
  }
}

export function setLabel(reflect: IReflect, value: any, reflectedItem: IdynamicFormItem) {
  if (!reflectedItem) {
    return;
  }

  if (reflect.labelToSet !== undefined) {
    reflectedItem.label = reflect.labelToSet;
  } else if (reflect.labelPath !== undefined) {
    reflectedItem.label = get(value, reflect.labelPath, ' ');
  }
}

export function setConfiguration(reflect: IReflect, value: any, reflectedItem: IdynamicFormItem) {
  if (!(reflectedItem && reflect.configurationOverride)) {
    return;
  }

  merge(reflectedItem, reflect.configurationOverride);
  reflectedItem['lastReflect'] = new Date().getTime().toString();
}

export function setPlaceholder(reflect: IReflect, value: any, reflectedItem: IdynamicFormItem) {
  if (!reflectedItem) {
    return;
  }

  if (reflect.placeholderToSet !== undefined) {
    reflectedItem.placeholder = reflect.placeholderToSet;
  } else if (reflect.placeholderPath !== undefined) {
    reflectedItem.placeholder = get(value, reflect.placeholderPath, ' ');
  }
}

export function setUpdateFormInstanceOnReflect(reflect: IReflect, reflectedItem: IdynamicFormItem) {
  if (!reflectedItem) {
    return;
  }

  if (reflect.updateFormInstanceOnReflect !== undefined) {
    reflectedItem.updateWidgetFormInstanceOnReflect = reflect.updateFormInstanceOnReflect;
  }
}

export function setOptions(reflect: IReflect, value: any, reflectedItem: IdynamicFormItem) {
  if (!reflectedItem) {
    return;
  }

  if (reflect.options !== undefined) {
    reflectedItem.options = reflect.options;
    reflectedItem['lastReflect'] = new Date().getTime().toString();
  }
}

export function setRemovePropertyFlag(reflect: IReflect, value: any, reflectedItem: IdynamicFormItem) {
  if (!reflectedItem) {
    return;
  }

  if (reflect.removeProperty !== undefined) {
    reflectedItem.additional.removed = reflect.removeProperty;
  }
}

export function getValueToSet(reflect: IReflect, value: any): { value: any, affectedField: string[] } {
  if (reflect.valuePath !== undefined) {
    value = reflect.valuePath === '' ? value : get(value, reflect.valuePath, '');
  } else if (reflect.transformValue !== undefined) {
    if (InterpolateService.hasInterpolationString(reflect.transformValue)) {
      value = FormulaService.evaluateFormula(reflect.transformValue, value);
    }
  } else if (reflect.valueToSet !== undefined) {
    value = reflect.valueToSet;
  } else if (!reflect.wholeFormReflection) {
    return null;
  }

  return {value, affectedField: reflect.affectedField as string[]};
}

export function getValueToSetFromReflectedItem(reflect: IReflect, value: any): { value: any, affectedField: string[] } {
  if (reflect.valuePathInReflectedItem !== undefined) {
    value = reflect.valuePathInReflectedItem === '' ? value : get(value, reflect.valuePathInReflectedItem, '');
    return {value, affectedField: reflect.affectedField as string[]};
  }

  return undefined;
}

export function getRequiredToSet(reflect: IReflect, value: any): boolean | null {
  let requiredToSet;

  if (reflect.requiredPath !== undefined) {
    requiredToSet = reflect.requiredPath === ''
      ? value
      : get(value, reflect.requiredPath);
  } else if (reflect.requiredToSet !== undefined) {
    requiredToSet = reflect.requiredToSet;
  } else {
    return null;
  }

  return requiredToSet;
}

export function setDisplayPattern(reflect: IReflect, value: any, reflectedItem: IdynamicFormItem) {
  if (!reflectedItem) {
    return;
  }

  if (reflect.displayPatternToSet !== undefined) {
    reflectedItem.displayPattern = reflect.displayPatternToSet;
  } else if (reflect.displayPatternPath !== undefined) {
    reflectedItem.label = get(value, reflect.displayPatternPath, ' ');
  }
}

export function setMinAndMaxAttributes(reflect: IReflect, value: any, reflectedItem: IdynamicFormItem) {
  if (!reflect.attributesOverride) {
    return;
  }

  if (reflect.attributesOverride) {
    if (reflect.attributesOverride['min'] && InterpolateService.hasInterpolationString(reflect.attributesOverride['min'])) {
      reflectedItem.attributes['min'] = <number>FormulaService.evaluateFormula(reflect.attributesOverride['min'], value);
    }

    if (reflect.attributesOverride['max'] && InterpolateService.hasInterpolationString(reflect.attributesOverride['max'])) {
      reflectedItem.attributes['max'] = <number>FormulaService.evaluateFormula(reflect.attributesOverride['max'], value);
    }
  }
}

export function applyValueTransformation(
  reflect: IReflect,
  value: any | Array<any>,
  reflectedItem: IdynamicFormItem
): { value: any, affectedField: string } {
  if (typeof value === 'string') {
    value = [value];
  }

  if (!value) {
    return {value, affectedField: reflectedItem.name};
  }

  const newValues = value.map(v => {
    const transformation = reflect.applyValuesTransformation.find(transform => {
      if (!transform.conditions) {
        return true;
      }

      return checkConditions(transform, v);
    });

    if (!transformation || !transformation.valueToSet) {
      return v;
    }

    return transformation.valueToSet;
  });

  return {value: newValues, affectedField: reflectedItem.name};
}

export function getValueToSetFromBulkValue(reflect: IReflect, reflectedItem: IdynamicFormItem) {
  if (reflect.bulkDefaultValueToItems) {
    return {value: reflect.bulkDefaultValueToItems[reflectedItem.name], affectedField: reflectedItem.name};
  }

  return null;
}

/**
 * Interpolating the Reflects based on the rawdata if interpolate key is true
 * @param interpolateObject
 * @param rawData
 */
export function fillInterpolateString(interpolateObject: IReflect, rawData: any) {
  return JSON.parse(InterpolateService.interpolate(JSON.stringify(interpolateObject), rawData));
}
